import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Siren, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import EmergencyChart from "@/components/EmergencyChart";

const KIND_AR = { medical: "حالة طبية", behavioral: "حالة سلوكية", injury: "إصابة", other: "طارئة" };

export default function EmergencyPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await api.get("/emergency");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    try {
      await api.patch(`/emergency/${id}/acknowledge`);
      toast.success("تم تأكيد استلام التنبيه");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="emergency-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-black text-slate-900">تنبيهات الطوارئ</h1>
        <p className="text-slate-500 mt-2">
          كل تنبيه يُرسل فوراً كإشعار واتساب لولي الأمر والإدارة
        </p>
      </div>

      {user.role === "admin" && <EmergencyChart />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <Card
            key={a.id}
            data-testid={`emergency-${a.id}`}
            className={`card-elevated overflow-hidden ${a.acknowledged ? "border-slate-200" : "border-rose-300 ring-2 ring-rose-100"}`}
          >
            <div className={`h-1.5 ${a.acknowledged ? "bg-slate-200" : "bg-rose-500 animate-pulse"}`} />
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.acknowledged ? "bg-slate-100 text-slate-600" : "bg-rose-100 text-rose-700"}`}>
                    <Siren className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-black text-slate-900">{a.child_name}</div>
                    <div className="text-xs text-slate-500">
                      {a.raised_by_name} · {new Date(a.timestamp).toLocaleString("ar-EG")}
                    </div>
                  </div>
                </div>
                <Badge className={a.acknowledged ? "bg-slate-200 text-slate-700 hover:bg-slate-200" : "bg-rose-600 text-white hover:bg-rose-600"}>
                  {KIND_AR[a.kind]}
                </Badge>
              </div>
              <div className={`text-sm p-3 rounded-lg whitespace-pre-wrap ${a.acknowledged ? "bg-slate-50 text-slate-700" : "bg-rose-50 text-rose-900 font-semibold"}`}>
                {a.message}
              </div>
              {a.acknowledged ? (
                <div className="mt-3 text-xs text-emerald-700 font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  تم التأكيد بواسطة {a.acknowledged_by}
                </div>
              ) : user.role === "admin" ? (
                <Button
                  data-testid={`ack-${a.id}`}
                  onClick={() => ack(a.id)}
                  className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> تأكيد الاستلام
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="md:col-span-2">
            <CardContent className="p-16 text-center text-slate-400">لا توجد تنبيهات — الحمد لله</CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
